import { ImageProps } from "next/image";
import React from "react";
import { MaterialType } from "components/common/Form/types";
import ArticleIcon from "images/article-icon.svg";
import AudioIcon from "images/audio-icon.svg";
import ChapterIcon from "images/chapter-icon.svg";
import InterviewIcon from "images/interview-icon.svg";
import TranscriptIcon from "images/transcript-icon.svg";
import VideoIcon from "images/video-icon.svg";
import { CourseViewQueryCourse } from "./__generated__/queries";
import styles from "./CourseMaterials.module.css";

type CourseMaterialsProps = {
  materials: CourseViewQueryCourse["materials"];
};

type CourseMaterialsAndTextProps = CourseMaterialsProps & {
  extraText: string | undefined | null;
};

type MaterialConfig = {
  icon: ImageProps["src"];
  text: string;
};

const MATERIALS_CONFIG: Record<MaterialType, MaterialConfig> = {
  Articles: {
    icon: ArticleIcon,
    text: "articles",
  },
  Audios: {
    icon: AudioIcon,
    text: "audio",
  },
  Videos: {
    icon: VideoIcon,
    text: "video",
  },
  Transcripts: {
    icon: TranscriptIcon,
    text: "transcripts",
  },
  Chapters: {
    icon: ChapterIcon,
    text: "chapters",
  },
  Interviews: {
    icon: InterviewIcon,
    text: "interviews",
  },
};

const CourseMaterialsLongNames: React.FC<CourseMaterialsProps> = ({ materials }) => {
  return (
    <>
      {materials.map((material, i) => {
        // @ts-expect-error: materials is a string from WP, but we only know about the types defined in MaterialType
        // all other types will be ignored
        const materialConfig = MATERIALS_CONFIG[material];

        if (!materialConfig) return null;

        let comma = "";

        if (i > 0) {
          comma = materials.length > 2 ? ", " : " ";

          if (i + 1 === materials.length) {
            comma += "and ";
          }
        }

        return (
          <React.Fragment key={material}>
            {comma}
            <span key={material}>{materialConfig.text}</span>
          </React.Fragment>
        );
      })}
    </>
  );
};

const ExtraText = ({ extraText }: { extraText: string | undefined | null }) => {
  const extraTextTrimmed = extraText?.trim() || "";

  return extraTextTrimmed.length > 0 ? <> {extraTextTrimmed}</> : null;
};

const CourseMaterialsForCoursePage: React.FC<CourseMaterialsAndTextProps> = ({
  materials,
  extraText,
}) => {
  if (materials.length === 0 && extraText?.trim().length === 0) {
    return null;
  }

  return (
    <div className={styles.courseMaterials}>
      <div className={styles.courseMaterialsLongNames}>
        {materials.length > 0 && (
          <>
            Course materials are available as <CourseMaterialsLongNames materials={materials} />.
          </>
        )}
        <ExtraText extraText={extraText} />
      </div>
    </div>
  );
};

export const CourseMaterialsForCatalog: React.FC<CourseMaterialsAndTextProps> = ({
  materials,
  extraText,
}) => {
  return (
    <>
      Course materials include <CourseMaterialsLongNames materials={materials} />.
      <ExtraText extraText={extraText} />
    </>
  );
};

export default CourseMaterialsForCoursePage;
